import React, { useCallback, useMemo, useRef, useState } from "react";
import { Close, DoubleArrow } from "@mui/icons-material";
import { accordionSummaryClasses, alpha, Box, Button, Chip, IconButton, InputAdornment, TextField, Theme, Typography } from "@mui/material";
import {
  CellMouseOutEvent,
  CellMouseOverEvent,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  INoRowsOverlayParams,
  RowClickedEvent,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";

import { MultiProjectArea, MultiProjectAreaStatistic } from "fond/types";
import { AgGrid } from "fond/widgets";

import { rankingColumns } from "../AssessPanel/rankingColumns";

import { FlexAccordion } from "./SelectableSubareaList.styles";

interface CustomNoRowsOverlayParams {
  hasFilter: boolean;
  onClearFilter: () => void;
  emptyMessage: string;
}

type NoRowsOverlayParams = INoRowsOverlayParams & CustomNoRowsOverlayParams;

const NoRowsOverlay: React.FC<NoRowsOverlayParams> = ({ hasFilter, onClearFilter, emptyMessage }) => {
  if (hasFilter) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 2, pointerEvents: "auto" }}>
        <Typography variant="caption" color="text.secondary">
          No matching rows
        </Typography>
        <Button size="small" variant="outlined" onClick={onClearFilter}>
          Clear filter
        </Button>
      </Box>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary">
      {emptyMessage}
    </Typography>
  );
};

interface SelectableSubareaListProps {
  /**
   * The list of all areas to display.
   */
  areas: MultiProjectArea[];
  /**
   * The multi project area statistics.
   */
  areaStatistics?: MultiProjectAreaStatistic[];
  /**
   * The IDs of areas currently selected by the user.
   */
  selectedIds: string[];
  /**
   * Callback fired when the selection changes. Receives the new array of selected IDs.
   */
  onSelectionChange: (selectedIds: string[]) => void;
  /**
   * Callback fired when the user clicks a row. Receives the clicked area.
   */
  onAreaClick?: (area: MultiProjectArea) => void;
  /**
   * Callback fired when the user hovers over or leaves an available area row.
   */
  onHoveredAreaChange?: (areaId: string | null) => void;
  /**
   * Flag indicating if the lists should be placed into loading mode.
   */
  loading?: boolean;
}

const baseGridOptions: GridOptions = {
  animateRows: false,
  rowGroupPanelShow: "never",
  sideBar: false,
  pagination: false,
  suppressMovableColumns: true,
  domLayout: "normal",
  alwaysShowVerticalScroll: true,
  selectionColumnDef: {
    width: 36,
    minWidth: 36,
    maxWidth: 36,
  },
  rowSelection: {
    mode: "multiRow",
    headerCheckbox: true,
    selectAll: "filtered",
  },
};

const SelectedNoRowsOverlay = () => (
  <Typography variant="caption" color="text.secondary">
    No subareas selected
  </Typography>
);

const getRowId = (params: GetRowIdParams<MultiProjectArea>) => params.data.ID;

/**
 * Valid event sources for user-initiated selection changes.
 * These are the only sources we want to react to for moving items between lists.
 */
const USER_SELECTION_SOURCES = ["checkboxSelected", "rowClicked", "uiSelectAll", "uiSelectAllFiltered"];

/**
 * Checks if the selection event was triggered by a user action (as opposed to programmatic selection).
 */
const isUserSelectionEvent = (event: SelectionChangedEvent<MultiProjectArea>): boolean => {
  return USER_SELECTION_SOURCES.includes(event.source);
};

const SelectableSubareaList: React.FC<SelectableSubareaListProps> = ({
  areas,
  areaStatistics,
  selectedIds,
  onSelectionChange,
  onAreaClick,
  onHoveredAreaChange,
  loading = false,
}) => {
  const selectedGridApi = useRef<GridApi | null>(null);
  const availableGridApi = useRef<GridApi | null>(null);
  const [quickFilter, setQuickFilter] = useState("");

  const selectedAreas = useMemo(() => areas.filter((area) => selectedIds.includes(area.ID)), [areas, selectedIds]);
  const availableAreas = useMemo(() => areas.filter((area) => !selectedIds.includes(area.ID)), [areas, selectedIds]);

  const gridContext = useMemo(() => ({ areaStatistics }), [areaStatistics]);

  const handleClearFilter = useCallback(() => {
    setQuickFilter("");
  }, []);

  const filteredAvailableAreas = useMemo(() => {
    if (!quickFilter) return availableAreas;
    const lowerFilter = quickFilter.toLowerCase();
    return availableAreas.filter((area) => area.Name.toLowerCase().includes(lowerFilter));
  }, [availableAreas, quickFilter]);

  const availableNoRowsOverlayParams = useMemo(
    () => ({
      hasFilter: quickFilter.length > 0,
      onClearFilter: handleClearFilter,
      emptyMessage: "All subareas have been selected",
    }),
    [quickFilter, handleClearFilter]
  );

  // When a row is unchecked in the "Selected" grid, remove it from selection.
  // Only react to user-initiated events — ignore programmatic selectAll() and data change events.
  const handleSelectedGridSelectionChanged = useCallback(
    (event: SelectionChangedEvent<MultiProjectArea>) => {
      if (!isUserSelectionEvent(event)) {
        return;
      }
      const stillSelected = event.api.getSelectedRows().map((row) => row.ID);
      const removed = selectedAreas.filter((area) => !stillSelected.includes(area.ID)).map((area) => area.ID);
      if (removed.length > 0) {
        onHoveredAreaChange?.(null);
        onSelectionChange(selectedIds.filter((id) => !removed.includes(id)));
      }
    },
    [selectedAreas, selectedIds, onSelectionChange, onHoveredAreaChange]
  );

  // When a row is checked in the "Available" grid, add it to selection.
  // Only react to user-initiated events.
  const handleAvailableGridSelectionChanged = useCallback(
    (event: SelectionChangedEvent<MultiProjectArea>) => {
      if (!isUserSelectionEvent(event)) {
        return;
      }
      const newlySelected = event.api.getSelectedRows().map((row) => row.ID);
      if (newlySelected.length > 0) {
        onHoveredAreaChange?.(null);
        onSelectionChange([...selectedIds, ...newlySelected]);
        // Clear selection in the available grid after moving items
        setTimeout(() => event.api.deselectAll(), 0);
      }
    },
    [selectedIds, onSelectionChange, onHoveredAreaChange]
  );

  // Pre-select all rows in the "Selected" grid when data updates
  const handleSelectedRowDataUpdated = useCallback((event: RowDataUpdatedEvent<MultiProjectArea>) => {
    event.api.selectAll();
  }, []);

  const handleSelectedGridReady = useCallback((event: GridReadyEvent) => {
    selectedGridApi.current = event.api;
    event.api.selectAll();
  }, []);

  const handleAvailableGridReady = useCallback((event: GridReadyEvent) => {
    availableGridApi.current = event.api;
  }, []);

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<MultiProjectArea>) => {
      if (event.data) {
        onAreaClick?.(event.data);
      }
    },
    [onAreaClick]
  );

  const handleAvailableCellMouseOver = useCallback(
    (event: CellMouseOverEvent<MultiProjectArea>) => {
      onHoveredAreaChange?.(event.data?.ID ?? null);
    },
    [onHoveredAreaChange]
  );

  const handleAvailableCellMouseOut = useCallback(
    (_event: CellMouseOutEvent<MultiProjectArea>) => {
      onHoveredAreaChange?.(null);
    },
    [onHoveredAreaChange]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Filter available areas by name"
        value={quickFilter}
        onChange={(e) => setQuickFilter(e.target.value)}
        InputProps={{
          endAdornment: quickFilter ? (
            <InputAdornment position="end">
              <IconButton aria-label="Clear subarea filter" size="small" edge="end" onClick={() => setQuickFilter("")}>
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <FlexAccordion
        label="Available areas"
        id="available-areas"
        defaultExpanded
        endAdornment={
          <Chip
            size="small"
            label={
              <Typography variant="body3" fontWeight={400}>
                {availableAreas.length}
              </Typography>
            }
          />
        }
      >
        <AgGrid
          columnDefs={rankingColumns}
          rowData={filteredAvailableAreas}
          getRowId={getRowId}
          context={gridContext}
          containerProps={{ flex: 1, minHeight: 0 }}
          loading={loading}
          size="compact"
          variant="borderless"
          gridOptions={baseGridOptions}
          onGridReady={handleAvailableGridReady}
          onSelectionChanged={handleAvailableGridSelectionChanged}
          onRowClicked={handleRowClicked}
          onCellMouseOver={handleAvailableCellMouseOver}
          onCellMouseOut={handleAvailableCellMouseOut}
          noRowsOverlayComponent={NoRowsOverlay}
          noRowsOverlayComponentParams={availableNoRowsOverlayParams}
        />
      </FlexAccordion>

      <DoubleArrow sx={{ alignSelf: "center", color: "text.secondary", transform: "rotate(90deg)" }} />

      <FlexAccordion
        label="Selected areas"
        id="selected-areas"
        defaultExpanded
        sx={{
          [`& .${accordionSummaryClasses.root}`]: {
            backgroundColor: (theme: Theme) => alpha(theme.palette.info.light, 0.12),
            color: "primary.main",
            [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
              color: "primary.main",
            },
          },
        }}
        endAdornment={
          <Chip
            size="small"
            label={
              <Typography variant="body3" fontWeight={400}>
                {selectedAreas.length}
              </Typography>
            }
          />
        }
      >
        <AgGrid
          columnDefs={rankingColumns}
          rowData={selectedAreas}
          getRowId={getRowId}
          context={gridContext}
          containerProps={{ flex: 1, minHeight: 0 }}
          loading={loading}
          size="compact"
          variant="borderless"
          gridOptions={baseGridOptions}
          onGridReady={handleSelectedGridReady}
          onRowDataUpdated={handleSelectedRowDataUpdated}
          onSelectionChanged={handleSelectedGridSelectionChanged}
          onRowClicked={handleRowClicked}
          onCellMouseOver={handleAvailableCellMouseOver}
          onCellMouseOut={handleAvailableCellMouseOut}
          noRowsOverlayComponent={SelectedNoRowsOverlay}
        />
      </FlexAccordion>
    </Box>
  );
};

export default SelectableSubareaList;
